import { useQuery } from "@tanstack/react-query";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { useScrollPastTracking } from "src/shared/hooks/useScrollTracking";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import { CarouselSkeleton } from "src/website-apps/responsive/stubhub-home/components/LazyCarouselSection/CarouselSkeleton";
import { RecentlyViewedWithRemove } from "src/website-apps/shared/components/grid/recently-viewed";
import { HOME_EVENT_NAMES } from "src/website-apps/shared/constants/EventNames";
import { useCarouselItemClickHandler } from "src/website-apps/shared/hooks/useCarouselItemClickHandler";

/**
 * Recently Viewed on Home carousel.
 *
 * Two data modes depending on experiment TMHomeProgressiveCarouselLoadingV2 (103068):
 *
 * - **Control** (or no experiment): `modelItems` is populated from the SSR page model
 *   (server fetched eagerly via GetRecentlyViewedV2) — always non-null, possibly with an
 *   empty output. The lazy query never fires in control, even when the model set is empty.
 *
 * - **Variant 1**: the server skips the UPS call and use-carousel-components fetches via
 *   GetRecentlyViewedItems at the registry level (the moveUp/dedupe position logic needs the
 *   data before this component mounts), then passes the fetched model down as `modelItems`.
 *   The query below shares that query key, so it acts as a same-request fallback while the
 *   registry fetch is in flight — React Query dedupes to a single network call.
 */
export const RecentlyViewedOnHome = ({ size, modelItems }) => {
  const createQueryFn = useCreateQueryFn();
  const hasModelData = (modelItems?.output?.length ?? 0) > 0;

  const { data: fetchedModel, isLoading } = useQuery({
    queryKey: createQueryKey(QueryKeyPrefix.RECENTLY_VIEWED_HOME),
    queryFn: createQueryFn({ method: "GetRecentlyViewedItems", params: [] }),
    staleTime: Infinity,
    enabled: !modelItems,
  });

  // Engages scroll past event tracking
  const recentlyViewedRef = useScrollPastTracking(
    HOME_EVENT_NAMES.SCROLLED_PAST_HOME,
    "scrolled_past_recently_viewed",
    true,
    0.8,
    true,
  );

  const handleOnClick = useCarouselItemClickHandler(HOME_EVENT_NAMES.RECENTLY_VIEWED_CAROUSEL);

  const source = hasModelData ? modelItems : fetchedModel;
  const recentlyViewedItems = source?.output?.length ? { ...source, items: source.output } : undefined;

  // Exp 103068 variant: while the lazy fetch is in flight (modelItems is null and the shared
  // query hasn't resolved) the registry keeps this slot open — hold it with a skeleton so
  // resolved content replaces it in place instead of popping in. In control modelItems is
  // always non-null, so the skeleton can never show. If the fetch resolves with no items the
  // skeleton collapses, same as the slot being hidden — the pre-skeleton behavior for that case.
  if (!recentlyViewedItems) {
    return !modelItems && isLoading ? <CarouselSkeleton /> : null;
  }

  return (
    <div ref={recentlyViewedRef}>
      <RecentlyViewedWithRemove
        carouselItemClickEvent={handleOnClick}
        recentlyViewedItemsFromModel={recentlyViewedItems}
        size={size}
      />
    </div>
  );
};
